import { useMemo } from 'react';

import { TableColumnsService } from '@/components/pages/reports/services/TableColumnsService';

// Hooks
import useFetchReports from '@/utils/hooks/swr/reports/useFetchReports';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils/classNames';

// Lib
import { PENDO_CLASS_REPORTS_DOWNLOAD_REPORT } from '@/lib/tools/pendo/class';

// Components
import TableList from '@/components/pages/reports/components/Table/TableList';
import TablePagination from '@/components/pages/reports/components/Table/TablePagination';
import TableWrapper from '@/components/pages/reports/components/Table/TableWrapper';
import TableFilters from '@/components/pages/reports/components/TableFilters';

const downloadFiles = (files) => {
    const downloadNextFile = (index) => {
        if (index >= files.length) {
            return;
        }
        const link = document.createElement('a');
        link.href = files[index];
        link.target = '_self';

        // Add a to the doc for click to work.
        (document.body || document.documentElement).appendChild(link);
        if (link.click) {
            link.click(); // The click method is supported by most browsers.
        }
        // Delete the temporary link.
        link.parentNode.removeChild(link);

        // Download the next file with a small timeout. The timeout is necessary
        // for IE, which will otherwise only download the first file.
        const nextFileIndex = index + 1;
        if (nextFileIndex <= files.length) {
            setTimeout(function () {
                downloadNextFile(nextFileIndex);
            }, 500);
        }
    };

    // Initiate the first download.
    downloadNextFile(0);
};

const HistoryReports = () => {
    const { t } = useTranslation('reports');
    const { reports, mutateReports, requestParams, setRequestParams, errorReports, isLoading } =
        useFetchReports({
            params: { per_page: 5, page: 1 },
            options: {
                refreshInterval: 30000 // add refresh interval to get reports when they're ready
            }
        });

    const downloadAllFiles = () => {
        const urls = reports?.data?.filter((report) => report?.url)?.map((report) => report?.url);
        downloadFiles(urls);
    };

    const columnsHistory = useMemo(() => TableColumnsService.getHistoryReportColumns(t), []);

    return (
        <div className="@container">
            <TableFilters
                reportType="history"
                requestParams={requestParams}
                setRequestParams={setRequestParams}
            />
            {reports?.data &&
            reports?.data?.length &&
            reports?.data?.filter((report) => report?.url)?.length ? (
                <div className="text-right">
                    <button
                        className={classNames(
                            'mb-2.5 text-xs text-icoBlue',
                            PENDO_CLASS_REPORTS_DOWNLOAD_REPORT
                        )}
                        onClick={downloadAllFiles}>
                        {t('reports:general.download_all')}
                    </button>
                </div>
            ) : null}
            <TableWrapper>
                <TableList
                    data={reports?.data ?? []}
                    isLoading={isLoading}
                    errorReports={errorReports}
                    mutateReports={mutateReports}
                    requestParams={requestParams}
                    setRequestParams={setRequestParams}
                    columns={columnsHistory}
                    initialState={{
                        hiddenColumns: ['frequency']
                    }}
                />
                <TablePagination
                    reports={reports}
                    isLoading={isLoading}
                    errorReports={errorReports}
                    requestParams={requestParams}
                    setRequestParams={setRequestParams}
                />
            </TableWrapper>
        </div>
    );
};

export default HistoryReports;
